import React, { createContext, useContext, useEffect, useState } from "react";
import { useLicenseDetails } from "./services";
import { PatientsContext } from "../Patients/PatientsContext";
import { get, sortBy } from "lodash";
import {
  LicenseContextType,
  LicenseDetail,
  UpdatedLicenseDetail,
} from "../../types/license-context";
import { useAuth } from "@qure/qure-auth";

export const LicenseContext = createContext<LicenseContextType>({
  licenseDetails: [],
  alertDetails: {} as UpdatedLicenseDetail,
  stickyAlert: undefined,
  trialLicenseDetails: {} as UpdatedLicenseDetail,
});

// LicenseContextProvider component
export const LicenseContextProvider = (props: { children: React.ReactNode }) => {
  const { children } = props;
  const { wsData }: any = useContext(PatientsContext);

  const { userInfo } = useAuth();
  const isLoggedIn = get(userInfo, ["authenticated"], false);
  const isTrialLicense = get(userInfo, ["is_trial"], false);
  const { data: licenseData }: any = useLicenseDetails(isLoggedIn);

  const [licenseDetails, setLicenseDetails] = useState<UpdatedLicenseDetail[]>([]);

  // Function to retrieve alert information based on license details
  function getAlertInformation(details: LicenseDetail[]): UpdatedLicenseDetail[] {
    let alertData: UpdatedLicenseDetail[] = [];

    // Iterate through each license detail
    for (let obj of details) {
      const { app_name, license_type, aggregate_details, is_license_started } = obj;

      // Skip processing if the license has not started

      let isAlert = false;
      let isPopupVisible = false;
      let showAlertAtTop = false;
      let remainingPercentage: number | undefined;
      let type = "";

      // Check alert conditions based on license type
      if (!is_license_started) {
        remainingPercentage = 100;
        isAlert = false;
        isPopupVisible = false;
        showAlertAtTop = false;
      } else if (license_type === "CONT") {
        type = "scans";
        // Calculate remaining percentage of scans
        remainingPercentage = Math.floor(
          (aggregate_details.total_scans_remaining / aggregate_details.total_scans) * 100
        );
        remainingPercentage = remainingPercentage < 0 ? 0 : remainingPercentage;

        // Set alert if remaining percentage is 20% or less
        isAlert = remainingPercentage <= 20;
        showAlertAtTop = remainingPercentage <= 10;

        // Show popup only if remaining percentage matches specific values (20%, 15%, or 10%)
        isPopupVisible = shouldTriggerPopup({
          remainingPercentage,
          license_type,
          app_name,
        }); //[20, 15, 10].includes(remainingPercentage);
      } else if (license_type === "TIME") {
        type = "days";
        const { total_days_remaining, total_days } = aggregate_details;

        remainingPercentage = Math.floor((total_days_remaining / total_days) * 100);
        remainingPercentage = remainingPercentage < 0 ? 0 : remainingPercentage;

        // Determine threshold for remaining days based on total days
        const threshold = total_days < 180 ? 15 : 90;

        // Set alert if remaining days are within the threshold
        isAlert = total_days_remaining <= threshold;

        // Show popup for specific remaining days:
        // - 15 days if total_days < 180
        // - 90, 60, or 30 days if total_days >= 180
        isPopupVisible = shouldTriggerPopup({
          remainingPercentage,
          license_type,
          total_days_remaining,
          total_days,
          app_name,
        });
        // (total_days < 180 && total_days_remaining === 15) ||
        // (total_days >= 180 && [90, 60, 30].includes(total_days_remaining));

        showAlertAtTop =
          (total_days < 180 && total_days_remaining <= 15) ||
          (total_days >= 180 && total_days_remaining <= 30);
      } else if (license_type === "PETL") {
        isPopupVisible = false;
        isAlert = false;
        remainingPercentage = 100;
      } else if (license_type === "COTI") {
        const { total_days_remaining, total_days, total_scans_remaining, total_scans } =
          aggregate_details;
        // Calculate remaining percentage of scans (similar to CONT)
        let remainingScansPercentage = Math.floor(
          (total_scans_remaining / total_scans) * 100
        );
        remainingScansPercentage =
          remainingScansPercentage < 0 ? 0 : remainingScansPercentage;

        let remainingDaysPercentage = Math.floor(
          (total_days_remaining / total_days) * 100
        );
        remainingDaysPercentage =
          remainingDaysPercentage < 0 ? 0 : remainingDaysPercentage;

        remainingPercentage = Math.min(remainingScansPercentage, remainingDaysPercentage);

        // Determine threshold for remaining days based on total days
        const threshold = total_days < 180 ? 15 : 90;
        // Set alert if remaining percentage is 20% or less
        isAlert = remainingScansPercentage <= 20 || total_days_remaining <= threshold;
        showAlertAtTop =
          remainingScansPercentage <= 10 ||
          (total_days < 180 && total_days_remaining <= 15) ||
          (total_days >= 180 && total_days_remaining <= 30);

        type = remainingScansPercentage <= 20 ? "scans" : "days";

        // Show popup for COTI with expiration handling
        isPopupVisible = shouldTriggerPopup({
          remainingPercentage: remainingScansPercentage,
          license_type,
          app_name,
          total_days_remaining,
          total_days,
        });
      }

      // If an alert is triggered, creating an alert object with the relevant details
      const _alertObj: UpdatedLicenseDetail = {
        app_name,
        isPopupVisible,
        license_type,
        is_license_started,
        remainingPercentage,
        isAlert,
        showAlertAtTop,
        type,
        isExpired:
          !is_license_started ||
          (aggregate_details?.in_grace_period && !aggregate_details?.expired)
            ? false
            : (license_type === "CONT" &&
                (aggregate_details?.total_scans_remaining === 0 ||
                  !!aggregate_details?.expired)) ||
              (license_type === "TIME" && !!aggregate_details?.expired) ||
              (license_type === "COTI" &&
                (!!aggregate_details?.expired ||
                  aggregate_details?.total_scans_remaining === 0)) ||
              false,
        ...aggregate_details,
      };

      alertData.push(_alertObj);
    }
    alertData = sortBy(alertData, "remainingPercentage");
    return alertData;
  }

  const shouldTriggerPopup = (licenseDetails: {
    remainingPercentage: number;
    license_type: string;
    total_days_remaining?: number;
    total_days?: number;
    app_name: string;
  }): boolean => {
    const _licenseAlertDismissed = localStorage.getItem("licenseAlertDismissed");
    let licenseAlertDismissed = _licenseAlertDismissed
      ? JSON.parse(_licenseAlertDismissed)
      : null;

    const {
      remainingPercentage,
      license_type,
      total_days_remaining = 0,
      total_days = 0,
      app_name,
    } = licenseDetails;

    if (!!licenseAlertDismissed && app_name === licenseAlertDismissed.app_name) {
      if (license_type === "CONT") {
        const dismissedPercentage = licenseAlertDismissed.remainingPercentage;
        if (
          (remainingPercentage <= 15 && dismissedPercentage > 15) ||
          (remainingPercentage <= 10 && dismissedPercentage > 10) ||
          remainingPercentage <= 0
        ) {
          localStorage.removeItem("licenseAlertDismissed");
          licenseAlertDismissed = null;
        }
      } else if (license_type === "TIME") {
        const dismissedRemainingDays = licenseAlertDismissed.total_days_remaining;
        if (
          (total_days_remaining <= 60 && dismissedRemainingDays > 60) ||
          (total_days_remaining <= 30 && dismissedRemainingDays > 30) ||
          total_days_remaining <= 0
        ) {
          localStorage.removeItem("licenseAlertDismissed");
          licenseAlertDismissed = null;
        }
      } else if (license_type === "COTI") {
        const dismissedPercentage = licenseAlertDismissed.remainingPercentage;
        const dismissedRemainingDays = licenseAlertDismissed.total_days_remaining;
        if (
          (remainingPercentage <= 15 && dismissedPercentage > 15) ||
          (remainingPercentage <= 10 && dismissedPercentage > 10) ||
          remainingPercentage <= 0 ||
          (total_days_remaining <= 60 && dismissedRemainingDays > 60) ||
          (total_days_remaining <= 30 && dismissedRemainingDays > 30) ||
          total_days_remaining <= 0
        ) {
          localStorage.removeItem("licenseAlertDismissed");
          licenseAlertDismissed = null;
        }
      }
    }

    let showPopup = false;
    if (license_type === "CONT") {
      if (
        remainingPercentage <= 20 &&
        remainingPercentage > 0 &&
        (!licenseAlertDismissed || app_name !== licenseAlertDismissed.app_name)
      ) {
        showPopup = true;
      }
    } else if (license_type === "TIME") {
      if (
        total_days > 180 &&
        total_days_remaining <= 90 &&
        total_days_remaining > 0 &&
        !licenseAlertDismissed
      ) {
        showPopup = true;
      }
    } else if (license_type === "COTI") {
      if (
        (remainingPercentage <= 20 &&
          remainingPercentage > 0 &&
          (!licenseAlertDismissed || app_name !== licenseAlertDismissed.app_name)) ||
        (total_days > 180 &&
          total_days_remaining <= 90 &&
          total_days_remaining > 0 &&
          !licenseAlertDismissed)
      ) {
        showPopup = true;
      }
    }
    return showPopup;
  };

  useEffect(() => {
    if (licenseData?.license_details) {
      const res = getAlertInformation(licenseData.license_details);
      setLicenseDetails(res);
    }
  }, [licenseData]);

  useEffect(() => {
    if (
      wsData &&
      (wsData.action === "license_details" || wsData.action === "license_renewed")
    ) {
      let licenseContent = wsData.content;
      if (licenseContent) {
        const res = getAlertInformation(licenseContent);
        setLicenseDetails(res);
      }
    }
  }, [wsData]);

  return (
    <LicenseContext.Provider
      value={{
        licenseDetails,
        trialLicenseDetails: isTrialLicense
          ? licenseDetails.find(
              (detail: UpdatedLicenseDetail) => detail.app_name === "xray"
            ) ?? ({} as UpdatedLicenseDetail)
          : ({} as UpdatedLicenseDetail),
        alertDetails:
          licenseDetails.find((detail: UpdatedLicenseDetail) => detail.isExpired) ??
          licenseDetails.find((detail: UpdatedLicenseDetail) => detail.isAlert) ??
          licenseDetails.find(
            (detail: UpdatedLicenseDetail) => detail?.in_grace_period
          ) ??
          ({} as UpdatedLicenseDetail),
        stickyAlert:
          licenseDetails.find((detail: UpdatedLicenseDetail) => detail.showAlertAtTop) ??
          undefined,
      }}
    >
      {children}
    </LicenseContext.Provider>
  );
};
